import { useNode } from "@craftjs/core";
import React from "react";
import { Box, Typography, ToggleButton, ToggleButtonGroup } from "@mui/material";
import FormatAlignLeftIcon from "@mui/icons-material/FormatAlignLeft";
import FormatAlignCenterIcon from "@mui/icons-material/FormatAlignCenter";
import FormatAlignRightIcon from "@mui/icons-material/FormatAlignRight";
import { selectedOutlineSx } from "../../config/settingsStyles";
import { SelectField, SliderField, CheckboxField, SettingsSection } from "../ui/form/SettingsField";
import { labelStyle, inputStyle } from "../../config/settingsStyles";

interface SocialLink {
  platform: string;
  url: string;
}

interface SocialLinksProps {
  links?: SocialLink[];
  iconSize?: number;
  gap?: number;
  alignment?: "left" | "center" | "right";
  showLabels?: boolean;
  style?: "icons" | "buttons" | "minimal";
  maxWidth?: number;
  paddingX?: number;
  paddingTop?: number;
  paddingBottom?: number;
}

// Image paths for social platforms with PNG icons
const socialIconImages: Record<string, string> = {
  facebook: "/static/images/social/facebook.png",
  instagram: "/static/images/social/ig.png",
  line: "/static/images/social/line.png",
  linkedin: "/static/images/social/linked.png",
  tiktok: "/static/images/social/tiktok.png",
};

// Social Icon component - uses PNG images where available, SVG fallback for others
const SocialIcon = ({ platform, size = 24 }: { platform: string; size?: number }) => {
  const iconSize = size * 0.6;

  // Use PNG image if available
  if (socialIconImages[platform]) {
    return (
      <img
        src={socialIconImages[platform]}
        alt={platform}
        style={{ width: iconSize, height: iconSize, objectFit: "contain" }}
      />
    );
  }

  // SVG fallback for platforms without PNG
  switch (platform) {
    case "whatsapp":
      return (
        <svg width={iconSize} height={iconSize} viewBox="0 0 24 24" fill="currentColor">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
        </svg>
      );
    case "twitter":
      return (
        <svg width={iconSize} height={iconSize} viewBox="0 0 24 24" fill="currentColor">
          <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
        </svg>
      );
    case "youtube":
      return (
        <svg width={iconSize} height={iconSize} viewBox="0 0 24 24" fill="currentColor">
          <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
        </svg>
      );
    case "email":
      return (
        <svg width={iconSize} height={iconSize} viewBox="0 0 24 24" fill="currentColor">
          <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z" />
        </svg>
      );
    case "phone":
      return (
        <svg width={iconSize} height={iconSize} viewBox="0 0 24 24" fill="currentColor">
          <path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z" />
        </svg>
      );
    default:
      return (
        <svg width={iconSize} height={iconSize} viewBox="0 0 24 24" fill="currentColor">
          <path d="M3.9 12c0-1.71 1.39-3.1 3.1-3.1h4V7H7c-2.76 0-5 2.24-5 5s2.24 5 5 5h4v-1.9H7c-1.71 0-3.1-1.39-3.1-3.1zM8 13h8v-2H8v2zm9-6h-4v1.9h4c1.71 0 3.1 1.39 3.1 3.1s-1.39 3.1-3.1 3.1h-4V17h4c2.76 0 5-2.24 5-5s-2.24-5-5-5z" />
        </svg>
      );
  }
};

const platformData: Record<string, { color: string; label: string }> = {
  facebook: { color: "#1877F2", label: "Facebook" },
  instagram: { color: "#E4405F", label: "Instagram" },
  line: { color: "#00B900", label: "LINE" },
  whatsapp: { color: "#25D366", label: "WhatsApp" },
  twitter: { color: "#000000", label: "X" },
  youtube: { color: "#FF0000", label: "YouTube" },
  tiktok: { color: "#000000", label: "TikTok" },
  linkedin: { color: "#0A66C2", label: "LinkedIn" },
  email: { color: "#EA4335", label: "Email" },
  phone: { color: "#34A853", label: "Phone" },
};

export const SocialLinks = ({
  links = [
    { platform: "facebook", url: "#" },
    { platform: "instagram", url: "#" },
    { platform: "line", url: "#" },
  ],
  iconSize = 40,
  gap = 12,
  alignment = "center",
  showLabels = false,
  style = "icons",
  maxWidth = 0,
  paddingX = 16,
  paddingTop = 16,
  paddingBottom = 16,
}: SocialLinksProps) => {
  const {
    connectors: { connect, drag },
    selected,
  } = useNode((state) => ({
    selected: state.events.selected,
  }));

  const alignmentMap = { left: "flex-start", center: "center", right: "flex-end" };

  return (
    <Box
      ref={(ref: HTMLDivElement | null) => {
        if (ref) connect(drag(ref));
      }}
      sx={{
        px: `${paddingX}px`,
        pt: `${paddingTop}px`,
        pb: `${paddingBottom}px`,
        ...(maxWidth > 0 && { maxWidth: `${maxWidth}px`, mx: "auto" }),
        ...(selected && selectedOutlineSx),
      }}
    >
      <Box sx={{ display: "flex", flexWrap: "wrap", justifyContent: alignmentMap[alignment], gap: `${gap}px` }}>
        {links.map((link, index) => {
          const platform = platformData[link.platform] || { color: "#666", label: link.platform };
          const hasPngIcon = !!socialIconImages[link.platform];

          if (style === "buttons") {
            return (
              <Box
                key={index}
                component="a"
                href={link.url}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  px: 2,
                  py: 1,
                  borderRadius: 2,
                  color: hasPngIcon ? "#333" : "#ffffff",
                  textDecoration: "none",
                  backgroundColor: hasPngIcon ? "#f3f4f6" : platform.color,
                  transition: "opacity 0.2s",
                  "&:hover": { opacity: 0.8 },
                }}
              >
                <SocialIcon platform={link.platform} size={iconSize} />
                {showLabels && (
                  <Typography component="span" sx={{ fontSize: "0.875rem", fontWeight: 500 }}>
                    {platform.label}
                  </Typography>
                )}
              </Box>
            );
          }

          if (style === "minimal") {
            return (
              <Box
                key={index}
                component="a"
                href={link.url}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  textDecoration: "none",
                  transition: "opacity 0.2s",
                  "&:hover": { opacity: 0.7 },
                }}
              >
                <SocialIcon platform={link.platform} size={iconSize} />
                {showLabels && (
                  <Typography component="span" sx={{ fontSize: "0.875rem", color: "#4b5563" }}>
                    {platform.label}
                  </Typography>
                )}
              </Box>
            );
          }

          // Default style: Circle icons
          return (
            <Box
              key={index}
              component="a"
              href={link.url}
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 0.5,
                textDecoration: "none",
                transition: "transform 0.2s",
                "&:hover": { transform: "scale(1.1)" },
              }}
            >
              {hasPngIcon ? (
                // PNG images already have circular backgrounds with colors
                <SocialIcon platform={link.platform} size={iconSize} />
              ) : (
                // SVG icons need a colored circle background
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    borderRadius: "50%",
                    width: `${iconSize}px`,
                    height: `${iconSize}px`,
                    backgroundColor: platform.color,
                    color: "#ffffff",
                  }}
                >
                  <SocialIcon platform={link.platform} size={iconSize} />
                </Box>
              )}
              {showLabels && <Typography sx={{ fontSize: "0.75rem", color: "#4b5563" }}>{platform.label}</Typography>}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

const SocialLinksSettings = () => {
  const {
    actions: { setProp },
    links,
    iconSize,
    gap,
    alignment,
    showLabels,
    style,
    maxWidth,
    paddingX,
    paddingTop,
    paddingBottom,
  } = useNode((node) => ({
    links: node.data.props.links,
    iconSize: node.data.props.iconSize,
    gap: node.data.props.gap,
    alignment: node.data.props.alignment,
    showLabels: node.data.props.showLabels,
    style: node.data.props.style,
    maxWidth: node.data.props.maxWidth,
    paddingX: node.data.props.paddingX,
    paddingTop: node.data.props.paddingTop,
    paddingBottom: node.data.props.paddingBottom,
  }));

  const availablePlatforms = Object.keys(platformData);

  const addLink = (platform: string) => {
    setProp((props: SocialLinksProps) => {
      props.links = [...(props.links || []), { platform, url: "#" }];
    });
  };

  const removeLink = (index: number) => {
    setProp((props: SocialLinksProps) => {
      props.links = props.links?.filter((_, i) => i !== index);
    });
  };

  const updateLink = (index: number, url: string) => {
    setProp((props: SocialLinksProps) => {
      if (props.links) props.links[index].url = url;
    });
  };

  return (
    <SettingsSection>
      <SelectField
        label="Style"
        value={style || "icons"}
        onChange={(v) => setProp((props: SocialLinksProps) => (props.style = v as SocialLinksProps["style"]))}
        options={[
          { value: "icons", label: "Circle Icons" },
          { value: "buttons", label: "Buttons" },
          { value: "minimal", label: "Minimal" },
        ]}
      />

      <Box>
        <Typography variant="body2" gutterBottom>
          Alignment
        </Typography>
        <ToggleButtonGroup
          value={alignment || "center"}
          exclusive
          onChange={(_, value) => {
            if (value !== null) {
              setProp((props: SocialLinksProps) => (props.alignment = value as SocialLinksProps["alignment"]));
            }
          }}
          size="small"
          fullWidth
        >
          <ToggleButton value="left" sx={{ flex: 1 }}>
            <FormatAlignLeftIcon fontSize="small" />
          </ToggleButton>
          <ToggleButton value="center" sx={{ flex: 1 }}>
            <FormatAlignCenterIcon fontSize="small" />
          </ToggleButton>
          <ToggleButton value="right" sx={{ flex: 1 }}>
            <FormatAlignRightIcon fontSize="small" />
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      <SliderField
        label="Icon Size"
        value={iconSize || 40}
        onChange={(v) => setProp((props: SocialLinksProps) => (props.iconSize = v))}
        min={24}
        max={64}
        unit="px"
      />

      <SliderField
        label="Gap"
        value={gap || 12}
        onChange={(v) => setProp((props: SocialLinksProps) => (props.gap = v))}
        min={4}
        max={32}
        unit="px"
      />

      <CheckboxField
        label="Show Labels"
        checked={showLabels || false}
        onChange={(v) => setProp((props: SocialLinksProps) => (props.showLabels = v))}
      />

      <SliderField
        label="Max Width"
        value={maxWidth || 0}
        onChange={(v) => setProp((props: SocialLinksProps) => (props.maxWidth = v))}
        min={0}
        max={1600}
        unit="px"
      />

      <SliderField
        label="Padding X"
        value={paddingX || 0}
        onChange={(v) => setProp((props: SocialLinksProps) => (props.paddingX = v))}
        min={0}
        max={120}
        unit="px"
      />

      <SliderField
        label="Padding Top"
        value={paddingTop || 0}
        onChange={(v) => setProp((props: SocialLinksProps) => (props.paddingTop = v))}
        min={0}
        max={120}
        unit="px"
      />

      <SliderField
        label="Padding Bottom"
        value={paddingBottom || 0}
        onChange={(v) => setProp((props: SocialLinksProps) => (props.paddingBottom = v))}
        min={0}
        max={120}
        unit="px"
      />

      <div>
        <label style={labelStyle}>Links</label>
        <div style={{ display: "flex", flexDirection: "column", gap: "8px", maxHeight: "192px", overflowY: "auto" }}>
          {links?.map((link: SocialLink, index: number) => {
            const platform = platformData[link.platform] || { color: "#666", label: link.platform };
            return (
              <div
                key={index}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  backgroundColor: "#f9fafb",
                  padding: "8px",
                  borderRadius: "4px",
                }}
              >
                <span style={{ color: platform.color, display: "flex", alignItems: "center" }}>
                  <SocialIcon platform={link.platform} size={24} />
                </span>
                <input
                  type="text"
                  value={link.url}
                  onChange={(e) => updateLink(index, e.target.value)}
                  style={{ ...inputStyle, flex: 1 }}
                  placeholder="URL"
                />
                <button
                  onClick={() => removeLink(index)}
                  style={{
                    color: "#dc2626",
                    fontSize: "0.875rem",
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                  }}
                >
                  ✕
                </button>
              </div>
            );
          })}
        </div>
        <div style={{ marginTop: "8px" }}>
          <label style={{ display: "block", fontSize: "0.75rem", color: "#6b7280", marginBottom: "4px" }}>
            Add Link:
          </label>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "4px" }}>
            {availablePlatforms.map((p) => (
              <button
                key={p}
                onClick={() => addLink(p)}
                style={{
                  padding: "4px 8px",
                  backgroundColor: "#f3f4f6",
                  borderRadius: "4px",
                  fontSize: "0.875rem",
                  border: "none",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  color: platformData[p].color,
                }}
                title={platformData[p].label}
              >
                <SocialIcon platform={p} size={20} />
              </button>
            ))}
          </div>
        </div>
      </div>
    </SettingsSection>
  );
};

SocialLinks.craft = {
  displayName: "Social Links",
  props: {
    links: [
      { platform: "facebook", url: "#" },
      { platform: "instagram", url: "#" },
      { platform: "line", url: "#" },
    ],
    iconSize: 40,
    gap: 12,
    alignment: "center",
    showLabels: false,
    style: "icons",
    maxWidth: 0,
    paddingX: 16,
    paddingTop: 16,
    paddingBottom: 16,
  },
  related: {
    settings: SocialLinksSettings,
  },
};
